import { disconnectBackoff } from './helpers/backoff';

const WS_READY_STATE_NAMES: Record<number, string> = {
  0: 'CONNECTING',
  1: 'OPEN',
  2: 'CLOSING',
  3: 'CLOSED',
};

// Watchdog: once at least one inbound message has been received, we expect
// further traffic within this window. If the window elapses without any new
// message, the underlying TCP connection is presumed dead (laptop sleep/wake,
// Chrome tab throttling, captive portals, NAT timeouts) and the socket is
// force-reconnected. AWS API Gateway WebSocket idle-times out at 10 min and
// emits no keep-alive of its own, so any 90 s silence on a subscribed
// connection is a strong signal.
const WATCHDOG_STALE_THRESHOLD_MS = 90_000;

type MessagePayload = {
  dateEmitted: string;
  topics: string[];
};

export default class WebSocketConnection extends EventTarget {
  private serverUrl: string;

  private retryCount = 0;

  private webSocket: WebSocket | null = null;

  private _lastTimestamp: number | null = null;

  /** Wall clock when we last parsed an inbound message (watchdog / gap logic). */
  private lastInboundWallClockMs: number | null = null;

  private watchdogTimerId: ReturnType<typeof setTimeout> | null = null;

  private useSecureHeader = true;

  private connectedWithSecureHeader = false;

  private fetchToken: () => Promise<string>;

  private queuedMessages: string[] = [];

  private reconnectTimerId: ReturnType<typeof setTimeout> | null = null;

  private connectingPromise: Promise<void> | null = null;

  private _shouldReconnect = true;

  constructor({
    serverUrl,
    fetchToken,
  }: {
    serverUrl: string;
    fetchToken: () => Promise<string>;
  }) {
    super();
    this.serverUrl = serverUrl;
    this.fetchToken = fetchToken;
  }

  get shouldReconnect(): boolean {
    return this._shouldReconnect;
  }

  set shouldReconnect(value: boolean) {
    this._shouldReconnect = Boolean(value);
  }

  async connect(options?: { force?: boolean }) {
    const force = options?.force === true;

    if (force) {
      if (this.webSocket) {
        const state = this.webSocket.readyState;
        if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
          // Tear down existing socket so we create a fresh one. Required when
          // the OS (e.g. mobile background) kills the TCP connection without
          // firing onclose — readyState can still be OPEN so connect() would
          // otherwise return early and never recover.
          this.webSocket.close();
          this.webSocket = null;
          if (this.reconnectTimerId !== null) {
            clearTimeout(this.reconnectTimerId);
            this.reconnectTimerId = null;
          }
          // Programmatic close is usually clean, so handleWebSocketDisconnect
          // returns without emitting connection-retry; the old socket's onclose
          // is also ignored once this.webSocket is replaced. Emit the same event
          // as the backoff path so listeners (e.g. inbox refetch after long gaps)
          // still run for watchdog / lifecycle force reconnects.
          this.dispatchEvent(
            new CustomEvent('connection-retry', {
              detail: {
                retryCount: this.retryCount,
                // Force reconnect runs while the socket can still look OPEN; the
                // hook measures gap from connection-retry to connected. Without
                // this, both fire within ~1s and CASES refetch never runs for
                // watchdog / lifecycle recovery paths.
                ...(this.lastInboundWallClockMs !== null
                  ? { gapStartTimestamp: this.lastInboundWallClockMs }
                  : {}),
              },
            }),
          );
        }
      }
      // Abandon any in-flight attempt — it could be stuck on a slow token
      // fetch or a socket we no longer want. A force caller must always
      // produce a fresh attempt.
      this.connectingPromise = null;
      this.clearWatchdog();
    }

    // If a connection attempt is already in-flight, wait for it instead of
    // creating a second socket.
    if (this.connectingPromise) {
      await this.connectingPromise;
      return;
    }
    const promise = this.performConnect();
    this.connectingPromise = promise;
    try {
      await promise;
    } finally {
      // Only clear the guard if this call's promise is still the active one.
      // If connect({ force: true }) ran concurrently, it nulled our promise and
      // set a new one; we must not clear the new caller's in-flight guard.
      if (this.connectingPromise === promise) {
        this.connectingPromise = null;
      }
    }
  }

  private async performConnect() {
    if (!this._shouldReconnect) {
      return;
    }
    if (this.webSocket?.readyState === WebSocket.OPEN) {
      console.warn('[WEBSOCKET] connection already established');
      return;
    }
    if (this.webSocket?.readyState === WebSocket.CONNECTING) {
      console.warn('[WEBSOCKET] connection already in progress');
      return;
    }

    // Clear any pending backoff reconnect so we don't create a second socket when the timer fires
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }

    const token = await this.fetchToken();

    // Re-check after the async gap — disconnect() may have been called while
    // the token was being fetched (e.g. during logout via cleanupWebSocketSingletons).
    // Without this guard we would create an orphaned WebSocket that stays open
    // even though the disconnect intent has already been signalled.
    if (!this._shouldReconnect) {
      return;
    }

    const url = this.useSecureHeader
      ? this.serverUrl
      : `${this.serverUrl}?jwt=${token}`;
    this.webSocket = new WebSocket(url, this.useSecureHeader ? [token] : []);
    this.webSocket.onopen = () => this.handleWebSocketOpen();
    this.webSocket.onclose = (event) => this.handleWebSocketDisconnect(event);
    this.webSocket.onmessage = (event) => this.handleMessage(event);
  }

  private handleWebSocketOpen() {
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
    console.info('[WEBSOCKET] connection state', this.webSocket?.readyState);
    this.retryCount = 0;
    if (!this.connectedWithSecureHeader && this.useSecureHeader) {
      this.connectedWithSecureHeader = true;
    }
    // Drain the message queue before dispatching 'connected' so that any
    // queued subscribe messages (with newConnection: false) are sent first.
    // Then the TopicManager's connectedHandler can send a single subscribe
    // with newConnection: true for recovery, which will be the final state
    // the backend sees. If we dispatched 'connected' first, the handler
    // would send newConnection: true and then the queue would send
    // newConnection: false, contradicting the recovery intent.
    while (this.queuedMessages.length > 0) {
      const message = this.queuedMessages.shift();
      if (message && this.webSocket) {
        this.webSocket.send(message);
      }
    }
    this.dispatchEvent(new Event('connected'));
  }

  private resetWatchdog() {
    this.clearWatchdog();
    this.watchdogTimerId = setTimeout(() => {
      this.watchdogTimerId = null;
      console.warn(
        `[WEBSOCKET] watchdog: no inbound message for >${WATCHDOG_STALE_THRESHOLD_MS}ms, forcing reconnect`,
      );
      // Force a fresh connection. Avoid awaiting from inside a timer callback;
      // any error is surfaced by the connect() promise chain / logs.
      this.connect({ force: true }).catch((error) => {
        console.error('[WEBSOCKET] watchdog reconnect failed:', error);
      });
    }, WATCHDOG_STALE_THRESHOLD_MS);
  }

  private clearWatchdog() {
    if (this.watchdogTimerId !== null) {
      clearTimeout(this.watchdogTimerId);
      this.watchdogTimerId = null;
    }
  }

  private handleWebSocketDisconnect(event: CloseEvent) {
    // Ignore close from a socket we've already replaced (e.g. after force reconnect).
    if (event.target !== this.webSocket) {
      return;
    }
    this.clearWatchdog();
    // Don't attempt reconnection if this was a manual disconnect
    if (!this._shouldReconnect) {
      console.log(
        '[WEBSOCKET] Manual disconnect detected, skipping reconnection',
      );
      return;
    }

    // 1001 means the connection was closed by the server for being idle
    // https://docs.aws.amazon.com/apigateway/latest/developerguide/apigateway-websocket-api-overview.html
    const closeWarrantsReconnect = !event.wasClean || event.code === 1001;
    if (!closeWarrantsReconnect) {
      return;
    }
    if (!event.wasClean) {
      console.warn(
        '[WEBSOCKET] connection closed unexpectedly with code: ',
        event.code,
        'reason: ',
        event.reason,
      );
      // currently some customer systems strip the JWT from the secure header
      // however, we don't have a way to know if the JWT is being stripped
      // so we will fallback to passing the JWT in the query parameter
      // on retries if we receive a dirty close
      if (!this.connectedWithSecureHeader) {
        this.useSecureHeader = false;
      }
    }
    const backoff = disconnectBackoff(this.retryCount);
    this.dispatchEvent(
      new CustomEvent('connection-retry', {
        detail: { retryCount: this.retryCount },
      }),
    );
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
    }
    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      this.connect();
    }, backoff);
    this.retryCount += 1;
  }

  private handleMessage(event: MessageEvent) {
    if (typeof event.data !== 'string') {
      console.error('Invalid message data type:', typeof event.data);
      return;
    }
    let parsedData: MessagePayload;
    try {
      parsedData = JSON.parse(event.data);
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
      return;
    }
    this.lastInboundWallClockMs = Date.now();
    // Any successfully-parsed inbound message is proof the socket is alive.
    // Reset the watchdog window so we only force-reconnect after a real lull.
    this.resetWatchdog();
    const { dateEmitted } = parsedData;
    if (dateEmitted) {
      const timestamp = new Date(parsedData.dateEmitted).getTime();
      if (
        !Number.isNaN(timestamp) &&
        (this._lastTimestamp === null || timestamp > this._lastTimestamp)
      ) {
        this._lastTimestamp = timestamp;
      }
    }
    this.dispatchEvent(new CustomEvent('message', { detail: parsedData }));
  }

  // any is ok here because we are using JSON.stringify
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  send(payload: any) {
    if (this.webSocket?.readyState === WebSocket.OPEN) {
      this.webSocket.send(JSON.stringify(payload));
    } else {
      this.queuedMessages.push(JSON.stringify(payload));
    }
  }

  disconnect() {
    this._shouldReconnect = false;
    this.queuedMessages = [];
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
    this.clearWatchdog();
    if (this.webSocket) {
      console.log('[WEBSOCKET] Manually disconnecting...');
      this.webSocket.close();
      this.webSocket = null;
      console.log('[WEBSOCKET] Disconnected');
    } else {
      console.log('[WEBSOCKET] No active connection to disconnect');
    }
  }

  get lastTimestamp(): number | null {
    return this._lastTimestamp;
  }

  /**
   * Get the current WebSocket connection status
   */
  getConnectionStatus() {
    if (!this.webSocket) {
      return { connected: false, readyState: null };
    }

    return {
      connected: this.webSocket.readyState === 1,
      readyState: WS_READY_STATE_NAMES[this.webSocket.readyState] ?? 'UNKNOWN',
    };
  }
}
